/**
 * Unit (vertical) authoring.
 *
 * A unit holds components — text, video, problems, and whatever else is installed. Two
 * halves, deliberately split:
 *
 *   THE LIST is ours.      `container_handler` and `container/…/children` are DRF, so the
 *                          JWT authenticates them, and we render the components, their
 *                          publish state and their actions in our own chrome.
 *
 *   THE EDITOR is Studio's. Each block type ships its own `studio_view`, third-party
 *                          blocks included, so the set of editors is open-ended. We embed
 *                          Studio's editor page in an iframe rather than reimplement an
 *                          open set — which also means a newly installed block type works
 *                          on day one with no change here.
 *
 * The iframe needs a CMS session, which the JWT cannot provide; `startStudioSession()`
 * below mints one. See `backend/openedx_thinlms_authoring/studio_session.py`.
 */

import { invalidateCsrfToken, openedx } from './openedx';

const V1 = '/api/contentstore/v1';

/* ------------------------------------------------------------------ unit context ---- */

/** One entry in the breadcrumb: course → section → subsection → this unit. */
export interface AncestorXBlock {
  children: { url: string; display_name: string; usage_key: string }[];
  title: string;
  is_last: boolean;
}

/**
 * A creatable component type.
 *
 * `category` is the XBlock type and `boilerplate_name` is an optional starting template —
 * "Blank Common Problem" and "Drag and Drop" are both `problem`-family entries that differ
 * only by boilerplate, so the pair has to travel together.
 */
export interface ComponentTemplate {
  display_name: string;
  category: string;
  boilerplate_name: string | null;
  tab: string;
  support_level: boolean | string;
  hinted: boolean;
}

/** Templates arrive grouped by family (Text, Video, Problem, Advanced, …). */
export interface ComponentTemplateGroup {
  type: string;
  display_name: string;
  templates: ComponentTemplate[];
  /** Absent on the "advanced" group, so genuinely optional rather than always present. */
  beta?: boolean;
  support_legend: { show_legend: boolean; allow_unsupported_xblocks: boolean };
}

export interface UnitContext {
  xblock: {
    display_name: string;
    category: string;
    /** The platform's own translated label, e.g. "Unit". Prefer it over a literal. */
    display_type: string;
  };
  /** Studio's own `create_xblock_info` for the unit — publish state, dates, staff lock. */
  xblock_info: Record<string, unknown>;
  ancestor_xblocks: AncestorXBlock[];
  component_templates: ComponentTemplateGroup[];
  position: number;
  /**
   * Neighbouring units, for prev/next navigation.
   *
   * quote_plus-ENCODED, not plain paths: the platform escapes them because they end up in
   * a query string on Studio's own page (`utils.py`, "quoting with mako will trigger the
   * xss linter"). So a value looks like `%2Fcontainer%2Fblock-v1%3A…`, and reading it as a
   * URL gives a 404. Use `siblingUnitKey` rather than the raw value.
   */
  prev_url: string | null;
  next_url: string | null;
  subsection_location: string;
  unit_block_id: string;
  draft_preview_link: string;
  published_preview_link: string;
  is_fullwidth_content: boolean;
  user_clipboard: Record<string, unknown>;
}

export const getUnitContext = (unitKey: string) =>
  openedx.get<UnitContext>(`${V1}/container_handler/${encodeURIComponent(unitKey)}`);

/* --------------------------------------------------------------------- children ---- */

/**
 * What a component permits.
 *
 * Named `can_*` here rather than the outline's `deletable`/`draggable` — the two APIs use
 * different vocabularies for the same idea, and normalising them would mean inventing a
 * third that matches neither payload.
 */
export interface ComponentActions {
  can_copy: boolean;
  can_duplicate: boolean;
  can_move: boolean;
  can_manage_access: boolean;
  can_delete: boolean;
  can_manage_tags: boolean;
}

export interface UnitChild {
  name: string;
  block_id: string;
  block_type: string;
  actions: ComponentActions;
  /**
   * Set when the component came from a v2 library.
   *
   * The full `UpstreamLink` — versions and local customisations included — so the row can offer
   * the sync decision without a second request per component.
   */
  upstream_link: {
    upstream_ref: string | null;
    ready_to_sync: boolean;
    version_synced?: number | null;
    version_available?: number | null;
    version_declined?: number | null;
    downstream_customized?: string[];
    error_message?: string | null;
  } | null;
  /** Platform-side validation, e.g. a problem with no correct answer. */
  validation_messages: { text: string; type: string }[];
  /** Non-empty when the block failed to render — shown instead of a blank row. */
  render_error: string;
}

export interface UnitChildren {
  children: UnitChild[];
  is_published: boolean;
  can_paste_component: boolean;
  display_name: string;
}

export const getUnitChildren = (unitKey: string) =>
  openedx.get<UnitChildren>(`${V1}/container/${encodeURIComponent(unitKey)}/children`);

/* ---------------------------------------------------------------------- writes ---- */

const XBLOCK = '/api/authoring/v1/xblock';

/**
 * Add a component.
 *
 * `boilerplate` is what separates "Blank Common Problem" from "Drag and Drop" — both are
 * created from a `problem`-family category and differ only by the template file, so
 * omitting it silently produces the wrong component.
 */
export const addComponent = (
  unitKey: string,
  category: string,
  boilerplateName: string | null,
) =>
  openedx.post<{ locator: string }>(XBLOCK, {
    parent_locator: unitKey,
    category,
    ...(boilerplateName ? { boilerplate: boilerplateName } : {}),
  });

/** Paste whatever is on Studio's clipboard. The platform tracks the clipboard per user. */
export const pasteComponent = (unitKey: string) =>
  openedx.post<{ locator: string }>(XBLOCK, {
    parent_locator: unitKey,
    staged_content: 'clipboard',
  });

/* ------------------------------------------------------------- the editor iframe ---- */

/**
 * Establish the CMS session the embedded editor needs.
 *
 * Studio's editor page is `@login_required`, which reads a Django session cookie — the JWT
 * that authenticates every other call here is invisible to it. This exchanges one for the
 * other, for the same user, and must resolve BEFORE the iframe is rendered: an iframe that
 * loads first gets redirected to a login page and stays there, because nothing reloads it
 * when the session later appears.
 *
 * AND IT DROPS THE CACHED CSRF TOKEN, because minting the session invalidates it. The
 * endpoint calls Django's `login()`, which calls `rotate_token()`, so the response carries a
 * NEW `csrftoken` cookie; the value `openedx.ts` cached when the page loaded is stale from
 * this moment on. Without this line the author's next save is rejected 403 and only succeeds
 * on the client's retry — invisible today, but it means every save in the product depends on
 * a recovery path, and a 403 that is really a CSRF rejection cannot be told apart from one
 * that is not. See `invalidateCsrfToken`.
 */
export const startStudioSession = async () => {
  const session = await openedx.post<{ authenticated_as: string; cookie: string }>(
    '/api/authoring/v1/studio-session',
    {},
  );
  invalidateCsrfToken();
  return session;
};

/**
 * URL of Studio's editor for one block.
 *
 * Same-origin on purpose. It is served through our own host so that (a) the CMS session
 * cookie is sent — a cross-site iframe would have it stripped as a third-party cookie —
 * and (b) Studio's `X_FRAME_OPTIONS = SAMEORIGIN` is satisfied. Pointing this at the
 * Studio host directly fails on both counts.
 */
export const editorUrl = (blockKey: string) =>
  `/xblock/${encodeURIComponent(blockKey)}/action/edit`;

/**
 * Usage key of a neighbouring unit, from a `prev_url`/`next_url` value.
 *
 * Decodes first — see the note on those fields — then takes the last path segment. Returns
 * null rather than a broken key so a caller renders no button instead of a dead one.
 */
export function siblingUnitKey(quoted: string | null): string | null {
  if (!quoted) return null;
  try {
    const path = decodeURIComponent(quoted.replace(/\+/g, ' ')).trim();
    const key = path.split('/').filter(Boolean).pop();
    return key && key.startsWith('block-v1:') ? key : null;
  } catch {
    return null;
  }
}

/**
 * Studio's chromeless rendering of a whole unit.
 *
 * This is the platform rendering its own components — the same fragments a learner will
 * see, with each block's own CSS and JavaScript — so an author previewing a unit sees the
 * truth rather than our approximation of it. Reimplementing that is not possible in the
 * general case: every XBlock ships its own view, third-party ones included.
 *
 * Same-origin through our proxy, for the same two reasons as the editor: the CMS session
 * cookie has to reach it, and `X_FRAME_OPTIONS = SAMEORIGIN` has to be satisfied. It needs
 * the session bridge first, exactly like the editor does.
 *
 * Unlike the editor page, this one DOES post `plugin.resize`, so the height can follow the
 * content instead of being guessed.
 */
export const unitPreviewUrl = (unitKey: string) =>
  `/container_embed/${encodeURIComponent(unitKey)}`;

/**
 * Messages Studio's editor page posts to us.
 *
 * Only two matter for an embedded single-block editor: how tall it wants to be, and that
 * the author has finished. The rest belong to the full container protocol, which we do not
 * use because we render the component list ourselves.
 */
export interface EditorMessage {
  type?: string;
  payload?: { width?: number; height?: number };
}

/**
 * `plugin.resize` is posted by `container_chromeless.html` and the v2 XBlock iframe — NOT
 * by the editor page we embed. It is matched anyway so the editor gets the right height if
 * a future release starts sending it, but the height cannot depend on it: the iframe is
 * sized in CSS, and a message only ever refines that.
 */
export function isResizeMessage(data: unknown): data is EditorMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as EditorMessage).type === 'plugin.resize' &&
    typeof (data as EditorMessage).payload?.height === 'number'
  );
}

/**
 * How Studio's editor says it is finished.
 *
 * These names are read from the code that actually posts them —
 * `cms/static/js/views/modals/edit_xblock.js` and `base_modal.js` — not guessed. An earlier
 * version of this file guessed, and every guess was wrong, which is worth recording:
 * `completeXBlockEditing` exists in the platform but travels the OTHER way, parent → iframe.
 * Matching on it meant the dialog never closed itself after a save.
 *
 *   saveEditedXBlockData    the author saved            (edit_xblock.js)
 *   closeXBlockEditorModal  cancelled, or closed after saving
 *   hideXBlockEditorModal   the modal hid itself
 */
const DONE_TYPES = new Set([
  'saveEditedXBlockData',
  'closeXBlockEditorModal',
  'hideXBlockEditorModal',
]);

export function isEditorDoneMessage(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  const type = (data as EditorMessage).type;
  return typeof type === 'string' && DONE_TYPES.has(type);
}

/**
 * Categories in `component_templates` that must NOT be created as blocks.
 *
 * `library_v2` is not an XBlock. It is a pseudo-category the platform puts in the palette
 * purely so Studio can render a tile that opens the library picker —
 * `contentstore/views/component.py` marks it "# Not an XBlock" and `_load_mixed_class` returns
 * None for it. Creating one produces a `HiddenBlock` with no display name: a junk row an author
 * cannot edit, delete meaningfully, or understand. (Two such blocks already exist in this
 * install from before this code, which is what the bug looks like in practice.)
 *
 * So the tile stays — it is a real thing an author wants — but picking it opens the picker
 * instead of posting a create.
 */
export const PICKER_CATEGORY = 'library_v2';

/**
 * Categories the platform still offers but has deprecated.
 *
 * `library_content` is `LegacyLibraryContentBlock`, whose own docstring says it is deprecated in
 * favour of v2 library references and asks that no new features be built on it. It is shown,
 * because an install may still contain them and hiding it would make existing content
 * unexplainable, but it is labelled so nobody reaches for it by accident. `itembank` ("Problem
 * Bank") is the modern replacement.
 */
export const DEPRECATED_CATEGORIES = new Set(['library_content']);

/**
 * Container types that hold LIBRARY CONTENT and render their children to the learner.
 *
 * Both of these are aggregates: their `student_view` renders their selected children and
 * NOTHING ELSE. `xmodule/item_bank_block.py` builds `vert_module.html` with `items` — for a
 * container with no children that is a wrapper with no content, so the learner gets a block
 * element containing zero characters. No heading, no message, no hint that anything is
 * missing. (`split_test`, the third container, at least says "Nothing here. Move along.")
 *
 * Measured on dev 2026-08-29, in a learner's frame, on a course whose author had created and
 * saved both from the palette:
 *
 *     data-block-type="itembank"         innerText length 0
 *     data-block-type="library_content"  innerText length 0
 *
 * So a container is only ever as useful as the author's ability to fill it, which is why this
 * set exists: the unit page owes these rows a content picker and an honest empty state, and
 * neither is something the block's own `studio_view` provides. That view is
 * `widgets/metadata-edit.html` — Count and Show Reset Button — while the picker Studio uses
 * lives in its `author_view` and is driven by `cms/static/js/views/pages/container.js`, a page
 * this app does not serve.
 */
export const CONTENT_CONTAINER_CATEGORIES = new Set(['itembank', 'library_content']);

/** Does this block type hold children that a learner sees, rather than content of its own? */
export const isContentContainer = (blockType: string) =>
  CONTENT_CONTAINER_CATEGORIES.has(blockType);

/** Icon for a component type. Falls back rather than guessing at an unknown block. */
export function componentIcon(blockType: string): string {
  switch (blockType) {
    case 'html':
      return 'article';
    case 'video':
      return 'play_circle';
    case 'problem':
      return 'quiz';
    case 'discussion':
      return 'forum';
    case 'openassessment':
      return 'rate_review';
    case 'drag-and-drop-v2':
      return 'drag_pan';
    case 'library_content':
    case 'itembank':
    // `library_v2` is the same idea under a third name — the tile that opens the library
    // picker (see PICKER_CATEGORY above). A course palette offers all three and a library
    // offers none of them, which is why this one was only ever seen with the generic icon.
    case 'library_v2':
      return 'library_books';
    default:
      return 'widgets';
  }
}

/**
 * Icons for the types `componentIcon` has no opinion about, which is most of the advanced
 * ones.
 *
 * A mapping, not a whitelist: an unknown third-party XBlock still lists and still works, it
 * just gets the generic icon. Nothing here is a guess — every key is a type this deployment
 * actually offers, and a type nobody has named gets `extension` rather than a wrong picture.
 *
 * Named for the LIBRARY because that is where it started, and it lived in `LibraryDetail`
 * until it was measured on the other surface. It was never library-specific: `poll`,
 * `survey`, `word_cloud`, `lti_consumer`, `pdf`, `google-document` and `google-calendar` are
 * all in the COURSE palette too, under Studio's Advanced family, with byte-identical
 * category strings. Verified on `SUST102`, whose 28 tiles carry exactly those identifiers.
 */
const EXTRA_BLOCK_ICONS: Record<string, string> = {
  poll: 'ballot',
  survey: 'fact_check',
  word_cloud: 'cloud',
  lti_consumer: 'link',
  lti: 'link',
  scorm: 'inventory',
  pdf: 'picture_as_pdf',
  done: 'check_circle',
  annotatable: 'sticky_note_2',
  conditional: 'call_split',
  'google-document': 'description',
  'google-calendar': 'event',
  edx_sga: 'upload_file',
};

/**
 * Icon for a block type on ANY authoring surface, and the only icon function a palette
 * should call.
 *
 * `componentIcon` is asked FIRST so a type that appears on both surfaces looks identical on
 * both. That rule used to be stated and then only half applied: `LibraryDetail` owned this
 * function privately and passed it to the palette as `iconFor`, and the course unit page did
 * not, so the same eight block types drew `ballot`, `fact_check`, `cloud`, `link`,
 * `picture_as_pdf`, `description` and `event` in a library and eight identical `widgets` in
 * a course. Same platform, same category strings, different page.
 *
 * The fallback is deliberately generic. An icon that is merely unhelpful costs an author a
 * moment of reading; an icon that is WRONG tells them the tile is something it is not, and
 * three types on this install are still unmapped on purpose — `staffgradedxblock`,
 * `split_test` and any XBlock an operator installs tomorrow.
 */
export function blockIcon(blockType: string): string {
  const shared = componentIcon(blockType);
  if (shared !== 'widgets') return shared;
  return EXTRA_BLOCK_ICONS[blockType] ?? 'extension';
}
